import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { CITY_LIST } from '../lib/cities.js';
import { usePageMeta } from '../lib/usePageMeta.js';
import { useSearchListings } from '../hooks/useSearch.js';
import type { SearchListing } from '@shared/api-types.js';

export function HomePage() {
  usePageMeta({
    title: 'Rent a trailer from a neighbour — Ottawa, Kingston, Perth, Brockville, Carleton Place, Peterborough',
    rawTitle: false,
    description:
      'Peer-to-peer trailer rental across Eastern Ontario. Utility, dump, enclosed, car-hauler, landscape, snowmobile, and motorcycle trailers from verified neighbours. Cheaper than U-Haul. Booked in 60 seconds.',
    canonicalPath: '/',
    keywords:
      'trailer rental Ottawa, trailer rental Kingston, trailer rental Perth, trailer rental Brockville, trailer rental Carleton Place, trailer rental Peterborough, utility trailer rental Ontario, peer to peer trailer, U-Haul alternative Ontario, TowShare',
  });

  return (
    <div>
      <section className="relative overflow-hidden bg-gradient-to-br from-navy via-navy-600 to-navy-700 text-white">
        <div className="absolute inset-0 opacity-10 pointer-events-none">
          <div
            className="absolute inset-0"
            style={{
              backgroundImage:
                'radial-gradient(circle at 20% 30%, #ff751f 0, transparent 40%), radial-gradient(circle at 80% 70%, #ff751f 0, transparent 30%)',
            }}
          />
        </div>
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-14 md:py-28">
          <p className="text-orange font-semibold uppercase tracking-wider text-sm mb-3">
            Your trailer, on demand
          </p>
          <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold tracking-tight max-w-3xl mb-4 leading-tight">
            Rent a trailer from a neighbor.
            <br />
            <span className="text-orange">Skip the U-Haul lineup.</span>
          </h1>
          <p className="text-base sm:text-lg md:text-xl text-navy-100 max-w-2xl mb-6 md:mb-8">
            Utility, dump, enclosed, car haulers — rent by the hour or day from people nearby.
            Cheaper than a rental yard. Booked in under 60 seconds.
          </p>
          <SearchBar />
          <div className="mt-8 md:mt-10 flex flex-wrap items-center gap-4 md:gap-6 text-sm text-navy-100">
            <div className="flex items-center gap-2">
              <Dot /> Verified renters &amp; owners
            </div>
            <div className="flex items-center gap-2">
              <Dot /> Insurance &amp; deposits built in
            </div>
            <div className="flex items-center gap-2">
              <Dot /> Stripe-powered secure payments
            </div>
          </div>
        </div>
      </section>

      <NearYouSection />

      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-20">
        <h2 className="text-3xl font-bold text-navy mb-2">Browse by trailer type</h2>
        <p className="text-navy-600 mb-10">Find exactly what you need within 10 km.</p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {TYPE_GRID.map((t) => (
            <Link
              key={t.slug}
              to={`/search?type=${t.slug}`}
              className="card p-6 hover:border-orange hover:shadow-md transition-all group"
            >
              <div className="text-3xl mb-3">{t.emoji}</div>
              <div className="font-semibold text-navy group-hover:text-orange transition-colors">
                {t.label}
              </div>
              <div className="text-sm text-navy-500 mt-1">{t.example}</div>
            </Link>
          ))}
        </div>
      </section>

      <section className="bg-navy-50/40 py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-navy mb-2">Trailer rental near you</h2>
          <p className="text-navy-600 mb-10">
            TowShare is focused on six Eastern and Central Ontario markets — pickups within 10&nbsp;km
            of your address in every one of them.
          </p>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            {CITY_LIST.map((c) => (
              <Link
                key={c.slug}
                to={`/trailer-rental/${c.slug}`}
                className="card p-4 hover:border-orange hover:shadow-md transition-all group"
              >
                <div className="font-semibold text-navy group-hover:text-orange transition-colors">
                  {c.name}
                </div>
                <div className="text-xs text-navy-500 mt-1">Trailer rental →</div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-10 items-center">
            <div>
              <p className="text-orange font-semibold uppercase tracking-wider text-sm mb-3">
                For owners
              </p>
              <h2 className="text-3xl md:text-4xl font-bold text-navy mb-4">
                Make $200–$600/month on a trailer that sits idle.
              </h2>
              <p className="text-navy-600 text-lg mb-6">
                Set your own price and availability. We handle payments, insurance, and renter
                verification. You handle the keys.
              </p>
              <Link to="/list-your-trailer" className="btn-primary">
                List your trailer free
              </Link>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <Stat label="Avg owner earnings" value="$340/mo" />
              <Stat label="Owner platform fee" value="10%" />
              <Stat label="Sign-up bonus" value="$100" />
              <Stat label="Payout speed" value="2–3 days" />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

function SearchBar() {
  return (
    <form
      action="/search"
      method="get"
      className="bg-white rounded-xl p-2 flex flex-col sm:flex-row gap-2 max-w-3xl shadow-2xl"
    >
      <input
        name="near"
        placeholder="City, postal code, or address"
        className="input border-none flex-1 text-navy"
        required
      />
      <button type="submit" className="btn-primary px-8">
        Search
      </button>
    </form>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="card p-5">
      <div className="text-2xl md:text-3xl font-bold text-navy">{value}</div>
      <div className="text-sm text-navy-500 mt-1">{label}</div>
    </div>
  );
}

function Dot() {
  return <span className="inline-block w-1.5 h-1.5 rounded-full bg-orange" />;
}

// Default fallback when the browser denies / can't determine location.
const DEFAULT_COORDS = { lat: 45.4215, lng: -75.6972 }; // Ottawa, ON

function NearYouSection() {
  const [coords, setCoords] = useState<{ lat: number; lng: number } | null>(null);
  const [locLabel, setLocLabel] = useState<string>('Eastern Ontario');

  useEffect(() => {
    if (!('geolocation' in navigator)) {
      setCoords(DEFAULT_COORDS);
      return;
    }
    let cancelled = false;
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        if (cancelled) return;
        setCoords({ lat: pos.coords.latitude, lng: pos.coords.longitude });
        setLocLabel('you');
      },
      () => {
        if (cancelled) return;
        setCoords(DEFAULT_COORDS);
      },
      { maximumAge: 10 * 60 * 1000, timeout: 4000 },
    );
    return () => {
      cancelled = true;
    };
  }, []);

  const search = useSearchListings(
    {
      lat: coords?.lat,
      lng: coords?.lng,
      radiusKm: 50,
      sort: 'nearest',
    } as never,
    Boolean(coords),
  );

  const listings = (search.data?.listings ?? []).slice(0, 12);
  if (!coords || listings.length === 0) return null;

  return (
    <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 pt-16 pb-4">
      <div className="flex items-end justify-between mb-6 gap-3">
        <div>
          <h2 className="text-2xl md:text-3xl font-bold text-navy">Trailers near {locLabel}</h2>
          <p className="text-navy-600 text-sm">Owners within 50 km. Tap a card to view.</p>
        </div>
        <Link
          to={`/search?lat=${coords.lat.toFixed(5)}&lng=${coords.lng.toFixed(5)}`}
          className="text-sm font-semibold text-orange hover:underline whitespace-nowrap"
        >
          See all →
        </Link>
      </div>
      <div className="-mx-4 sm:mx-0 overflow-x-auto pb-4">
        <div className="flex gap-4 px-4 sm:px-0">
          {listings.map((l) => (
            <NearYouCard key={l.id} listing={l} />
          ))}
        </div>
      </div>
    </section>
  );
}

function NearYouCard({ listing }: { listing: SearchListing }) {
  const photo = listing.photos?.[0];
  return (
    <Link
      to={`/listings/${listing.slug}`}
      className="group flex-shrink-0 w-60 card overflow-hidden hover:border-orange transition-colors"
    >
      <div className="aspect-[4/3] bg-navy-50 overflow-hidden">
        {photo ? (
          <img
            src={photo.thumbnailUrl || photo.url}
            alt={listing.title}
            loading="lazy"
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-navy-300 text-3xl">
            🛻
          </div>
        )}
      </div>
      <div className="p-3">
        <div className="text-sm font-semibold text-navy truncate">{listing.title}</div>
        <div className="text-xs text-navy-500 mt-0.5">
          {listing.city}
          {typeof listing.distanceKm === 'number' &&
            ` · ${listing.distanceKm.toFixed(1)} km`}
        </div>
        <div className="text-sm font-bold text-navy mt-1.5">
          ${Math.round(listing.dailyRateCents / 100)}
          <span className="text-xs font-normal text-navy-500"> / day</span>
        </div>
      </div>
    </Link>
  );
}

const TYPE_GRID = [
  { slug: 'utility', label: 'Utility', emoji: '🛻', example: 'Dump runs, moving, hauls' },
  { slug: 'dump', label: 'Dump', emoji: '🪨', example: 'Landscaping, demo, debris' },
  { slug: 'enclosed', label: 'Enclosed', emoji: '📦', example: 'Furniture, gear, gigs' },
  { slug: 'car_hauler', label: 'Car hauler', emoji: '🚗', example: 'Project cars, ATVs' },
  { slug: 'landscape', label: 'Landscape', emoji: '🌿', example: 'Mulch, soil, equipment' },
  { slug: 'flatbed', label: 'Flatbed', emoji: '🏗️', example: 'Lumber, drywall, skids' },
  { slug: 'snowmobile', label: 'Snowmobile', emoji: '❄️', example: 'Sled transport' },
  { slug: 'motorcycle', label: 'Motorcycle', emoji: '🏍️', example: 'Bikes, dirt bikes' },
];
